import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import PlanModal from '../../PlanModal/PlanModal';
import { FlatsFiltersContentContext } from '../../FlatsFilters/FlatsFiltersContext';
import { getCurrencyLabel, isCurrencyUsd } from '../../helpers/currency';
import { attachGaDataAttributes } from '../../helpers/analytics';
import RoomsIcon from '../../Plans/PlansCard/icons/rooms.svg';
import PlansGroupIcon from '../../Plans/PlansCard/icons/plans-group.svg';
import AreaIcon from '../../Plans/PlansCard/icons/area.svg';
import HouseIcon from '../../Plans/PlansCard/icons/house.svg';
import FloorsIcon from '../../Plans/PlansCard/icons/floors.svg';
import DateIcon from '../../Plans/PlansCard/icons/date.svg';
import StateIcon from '../../Plans/PlansCard/icons/state.svg';

const formatPrice = (price) => price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const FlatsCard = ({
  layoutId,
  flatId,
  image,
  vectorLayout,
  imageAlt,
  isSold,
  areaTotal,
  roomCount,
  priceTotalUah,
  priceTotalUsd,
  sqmPriceRangeUah,
  sqmPriceRangeUsd,
  developerFlatNumber,
  floorNumber,
  floorsCount,
  house,
  date,
  state,
  url,
}) => {
  const { currency } = useContext(FlatsFiltersContentContext);
  const isUsd = isCurrencyUsd(currency);
  const currencyLabel = getCurrencyLabel(currency);
  const imageSrc = {
    src: vectorLayout || image.x1,
  };
  let minPrice;
  let maxPrice;

  if (sqmPriceRangeUsd && sqmPriceRangeUah) {
    minPrice = formatPrice(isUsd ? sqmPriceRangeUsd.minPrice : sqmPriceRangeUah.minPrice);
    maxPrice = formatPrice(isUsd ? sqmPriceRangeUsd.maxPrice : sqmPriceRangeUah.maxPrice);
  }

  if (!vectorLayout) {
    imageSrc.srcSet = `${image.x2} 2x`;
  }

  const handleClick = (event) => {
    event.preventDefault();
    new PlanModal(
      {
        modalId: flatId,
        requestUrl: '/api/layout-pro-modal',
        openedModalKey: 'flatId',
      },
      {
        additionalParams: { layout_id: layoutId },
      },
    );
  };

  return (
    <div className={cx('PlansCard', { '-muted': isSold })}>
      <div className="PlansCard-inner">
        {isSold && (
          <div className="Label -grey">
            <FormattedMessage id="card.sold" />
          </div>
        )}
        <img className="PlansCard-image" {...imageSrc} alt={imageAlt} />
      </div>
      <div className="PlansCard-content">
        {priceTotalUah && priceTotalUsd && (
          <div className="PlansCard-title">
            {isUsd
              ? `${currencyLabel} ${formatPrice(priceTotalUsd)}`
              : `${formatPrice(priceTotalUah)} ${currencyLabel}`}
          </div>
        )}
        {minPrice && maxPrice && (
          <div className="PlansCard-title">
            {isUsd
              ? `${currencyLabel} ${minPrice} — ${maxPrice} за м²`
              : `${minPrice} — ${maxPrice} ${currencyLabel} за м²`}
          </div>
        )}
        <div className="PlansCard-items">
          <div className="PlansCard-column">
            <div className="PlansCard-item">
              <RoomsIcon />
              <div className="PlansCard-item-description">
                <FormattedMessage id="card.roomsCount" values={{ roomCount }} />
              </div>
            </div>
            <div className="PlansCard-item">
              <AreaIcon />
              <div className="PlansCard-item-description">{`${areaTotal} м²`}</div>
            </div>
            {floorNumber && (
              <div className="PlansCard-item">
                <FloorsIcon />
                <div className="PlansCard-item-description">
                  {floorsCount ? (
                    <FormattedMessage
                      id="card.floorNumberWithFloorsCount"
                      values={{ floorNumber, floorsCount }}
                    />
                  ) : (
                    <FormattedMessage id="card.floorNumber" values={{ floorNumber }} />
                  )}
                </div>
              </div>
            )}
            {developerFlatNumber && (
              <div className="PlansCard-item">
                <PlansGroupIcon />
                <div className="PlansCard-item-description">
                  <FormattedMessage
                    id="card.developerFlatNumber"
                    values={{ developerFlatNumber }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="PlansCard-column">
            {house && (
              <div className="PlansCard-item">
                <HouseIcon />
                <div className="PlansCard-item-description">{house}</div>
              </div>
            )}
            {date && (
              <div className="PlansCard-item">
                <DateIcon />
                <div className="PlansCard-item-description">{date}</div>
              </div>
            )}
            {state && (
              <div className="PlansCard-item">
                <StateIcon />
                <div className="PlansCard-item-description">{state}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <a
        className="PlansCard-link"
        onClick={handleClick}
        href={url}
        {...attachGaDataAttributes({
          category: window.eventCategory,
          action: 'flats_list',
          label: 'goto_view_flat',
        })}
      />
    </div>
  );
};

FlatsCard.propTypes = {
  layoutId: PropTypes.number.isRequired,
  flatId: PropTypes.number.isRequired,
  image: PropTypes.shape({
    x1: PropTypes.string,
    x2: PropTypes.string,
  }).isRequired,
  vectorLayout: PropTypes.string,
  imageAlt: PropTypes.string,
  isSold: PropTypes.bool,
  areaTotal: PropTypes.number.isRequired,
  developerFlatNumber: PropTypes.string,
  floorNumber: PropTypes.number.isRequired,
  floorsCount: PropTypes.number,
  roomCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  priceTotalUah: PropTypes.number,
  priceTotalUsd: PropTypes.number,
  sqmPriceRangeUah: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
  }),
  sqmPriceRangeUsd: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
  }),
  isReady: PropTypes.bool,
  house: PropTypes.string,
  date: PropTypes.string,
  state: PropTypes.string,
  url: PropTypes.string,
};

export default FlatsCard;
